import {
  COMMENT_MAX_LENGTH,
  normalizeCommentSort,
  sanitizeCommentInput,
  sanitizeReportInput
} from "../../shared/marketComments.js";
import { buildMarketSocialProof, displayIdentityFor } from "../../shared/marketSocialProof.js";
import { buildMarketVoteHistory } from "../../shared/marketVoteHistory.js";
import { buildSeasonZeroParticipants } from "../../shared/seasonZeroContent.js";

const now = Date.now();
const oneHour = 60 * 60 * 1000;
const initialMarketId = "mk-default-1";
const initialVotes = [
  {
    id: "vote-1",
    marketId: initialMarketId,
    optionId: "opt-1",
    userId: "user-a",
    voteAmount: 0.25,
    supportCommunitySlug: "kreechures",
    supportCommunityName: "Kreechures",
    collectionLamports: 1000000,
    createdAt: new Date(now - 45 * 60 * 1000).toISOString()
  },
  {
    id: "vote-2",
    marketId: initialMarketId,
    optionId: "opt-2",
    userId: "user-b",
    voteAmount: 0.1,
    supportCommunitySlug: "kreechures",
    supportCommunityName: "Kreechures",
    collectionLamports: 1000000,
    createdAt: new Date(now - 38 * 60 * 1000).toISOString()
  },
  {
    id: "vote-3",
    marketId: initialMarketId,
    optionId: "opt-1",
    userId: "user-c",
    voteAmount: 0.99,
    supportCommunitySlug: "kreechures",
    supportCommunityName: "Kreechures",
    collectionLamports: 1000000,
    createdAt: new Date(now - 22 * 60 * 1000).toISOString()
  },
  {
    id: "vote-4",
    marketId: initialMarketId,
    optionId: "opt-2",
    userId: "user-d",
    voteAmount: 0.78,
    supportCommunitySlug: "kreechures",
    supportCommunityName: "Kreechures",
    collectionLamports: 1000000,
    createdAt: new Date(now - 12 * 60 * 1000).toISOString()
  }
];
let nextCommentNumber = 2;

export const mockStore = {
  users: new Map(),
  userAffiliations: new Map(),
  markets: [
    {
      id: initialMarketId,
      slug: "will-space-tourism-scale",
      title: "Will space tourism scale by 2030?",
      summary: "Vote on whether commercial space tourism reaches mainstream adoption by 2030.",
      supportCommunitySlug: "kreechures",
      supportCommunityName: "Kreechures",
      creatorWallet: "user-creator",
      xUrl: "https://x.com/polymarket",
      websiteUrl: "https://polymarket.com",
      ogImageUrl: "https://images.raiziq.com/market-og/v3/public/mk-default-1.png",
      startAt: new Date(now - oneHour).toISOString(),
      endAt: new Date(now + 7 * 24 * oneHour).toISOString(),
      status: "active",
      options: [
        { id: "opt-1", label: "Yes" },
        { id: "opt-2", label: "No" }
      ],
      totals: {
        "opt-1": 1.24,
        "opt-2": 0.88
      }
    }
  ],
  votes: initialVotes,
  comments: [
    {
      id: "comment-1",
      marketId: initialMarketId,
      walletAddress: "user-a",
      body: "The early vote split makes this feel closer than the headline does.",
      authorPositionLabel: "Yes",
      likeCount: 3,
      viewerLiked: false,
      createdAt: new Date(now - 8 * 60 * 1000).toISOString(),
      moderationStatus: "visible"
    }
  ],
  commentLikes: new Set(),
  commentReports: []
};

export function computeNotableEvents({ since = null, limit = 20 } = {}) {
  const sinceTime = since ? new Date(since).getTime() : 0;
  const events = [
    {
      id: "mock-notable-first-market",
      eventType: "market_created",
      source: "system",
      message: "First market created today: Will space tourism scale by 2030?",
      marketId: initialMarketId,
      href: `/market/${initialMarketId}`,
      visible: true,
      metadata: { marketTitle: "Will space tourism scale by 2030?" },
      occurredAt: new Date(now - 2 * 60 * 1000).toISOString(),
      createdAt: new Date(now - 2 * 60 * 1000).toISOString(),
      updatedAt: new Date(now - 2 * 60 * 1000).toISOString()
    },
    {
      id: "mock-notable-vote",
      eventType: "vote_cast",
      source: "system",
      message: "user-c moved 0.99 SOL to Yes in Will space tourism scale by 2030?",
      marketId: initialMarketId,
      href: `/market/${initialMarketId}`,
      visible: true,
      metadata: { marketTitle: "Will space tourism scale by 2030?", optionLabel: "Yes", voteAmount: 0.99 },
      occurredAt: new Date(now - 8 * 60 * 1000).toISOString(),
      createdAt: new Date(now - 8 * 60 * 1000).toISOString(),
      updatedAt: new Date(now - 8 * 60 * 1000).toISOString()
    }
  ];

  return events
    .filter((event) => new Date(event.occurredAt).getTime() >= sinceTime)
    .sort((left, right) => new Date(right.occurredAt).getTime() - new Date(left.occurredAt).getTime())
    .slice(0, Math.max(1, Math.min(Number(limit) || 20, 50)));
}

export function computeMarketStats(market) {
  const totalCents = Object.values(market.totals).reduce((acc, current) => acc + toCents(current), 0);
  const totalVotes = fromCents(totalCents);
  const options = market.options.map((option) => {
    const votesCents = toCents(market.totals[option.id] || 0);
    const votes = fromCents(votesCents);
    const percentage = totalCents === 0 ? 0 : Math.round((votesCents / totalCents) * 100);
    return {
      ...option,
      votes,
      percentage
    };
  });

  return {
    ...market,
    totalVotes,
    options
  };
}

export function computeLeaderboards() {
  const globalVotesByUser = new Map();
  const nowTs = Date.now();
  const hourAgo = nowTs - oneHour;
  const dayStart = new Date();
  dayStart.setUTCHours(0, 0, 0, 0);
  const weekStart = startOfUtcWeek(nowTs);

  const dayVotesByUser = new Map();
  const hourVotesByUser = new Map();
  const marketVotes = new Map();
  const marketById = new Map(mockStore.markets.map((market) => [market.id, market]));
  const affiliateAllTime = new Map();
  const affiliateDay = new Map();
  const affiliateHour = new Map();
  const affiliateUsersAllTime = new Map();
  const affiliateUsersDay = new Map();
  const affiliateUsersHour = new Map();
  const communityAllTime = new Map();
  const communityDay = new Map();
  const communityWeek = new Map();

  for (const vote of mockStore.votes) {
    const uid = vote.userId;
    const amountCents = toCents(vote.voteAmount);
    globalVotesByUser.set(uid, (globalVotesByUser.get(uid) || 0) + amountCents);

    const voteTs = new Date(vote.createdAt).getTime();
    if (voteTs >= dayStart.getTime()) {
      dayVotesByUser.set(uid, (dayVotesByUser.get(uid) || 0) + amountCents);
    }
    if (voteTs >= hourAgo) {
      hourVotesByUser.set(uid, (hourVotesByUser.get(uid) || 0) + amountCents);
    }

    const community = communityForVote(vote, marketById.get(vote.marketId));
    addCommunitySupport(communityAllTime, community, vote);
    if (voteTs >= dayStart.getTime()) {
      addCommunitySupport(communityDay, community, vote);
    }
    if (voteTs >= weekStart.getTime()) {
      addCommunitySupport(communityWeek, community, vote);
    }

    const marketKey = `${vote.marketId}:${uid}`;
    marketVotes.set(marketKey, (marketVotes.get(marketKey) || 0) + amountCents);

    if (vote.affiliateUserId) {
      affiliateAllTime.set(vote.affiliateUserId, (affiliateAllTime.get(vote.affiliateUserId) || 0) + amountCents);
      addDistinctUser(affiliateUsersAllTime, vote.affiliateUserId, uid);

      if (voteTs >= dayStart.getTime()) {
        affiliateDay.set(vote.affiliateUserId, (affiliateDay.get(vote.affiliateUserId) || 0) + amountCents);
        addDistinctUser(affiliateUsersDay, vote.affiliateUserId, uid);
      }

      if (voteTs >= hourAgo) {
        affiliateHour.set(vote.affiliateUserId, (affiliateHour.get(vote.affiliateUserId) || 0) + amountCents);
        addDistinctUser(affiliateUsersHour, vote.affiliateUserId, uid);
      }
    }
  }

  const usersLookup = mockStore.users;

  const asRank = (mapObj) =>
    Array.from(mapObj.entries())
      .map(([userId, votesCents]) => ({
        userId,
        nickname: usersLookup.get(userId)?.nickname || null,
        votes: fromCents(votesCents)
      }))
      .sort((a, b) => b.votes - a.votes)
      .slice(0, 20);

  const market = {};
  for (const [combined, votesCents] of marketVotes.entries()) {
    const [marketId, userId] = combined.split(":");
    market[marketId] ||= [];
    market[marketId].push({
      userId,
      nickname: usersLookup.get(userId)?.nickname || null,
      votes: fromCents(votesCents)
    });
  }

  for (const marketId of Object.keys(market)) {
    market[marketId].sort((a, b) => b.votes - a.votes);
    market[marketId] = market[marketId].slice(0, 20);
  }

  return {
    global: asRank(globalVotesByUser),
    day: asRank(dayVotesByUser),
    hour: asRank(hourVotesByUser),
    market,
    affiliate: {
      allTime: asAffiliateRank(affiliateAllTime, affiliateUsersAllTime, usersLookup),
      day: asAffiliateRank(affiliateDay, affiliateUsersDay, usersLookup),
      hour: asAffiliateRank(affiliateHour, affiliateUsersHour, usersLookup)
    },
    community: {
      day: asCommunityRank(communityDay),
      week: asCommunityRank(communityWeek),
      allTime: asCommunityRank(communityAllTime)
    }
  };
}

export function computeMarketVoteHistory(marketId) {
  const market = mockStore.markets.find((item) => item.id === marketId || item.slug === marketId);
  if (!market) return null;

  return buildMarketVoteHistory({
    market: {
      id: market.id,
      startAt: market.startAt,
      endAt: market.endAt
    },
    options: market.options,
    votes: mockStore.votes
      .filter((vote) => vote.marketId === market.id)
      .map((vote) => ({
        id: vote.id,
        optionId: vote.optionId,
        voteAmount: vote.voteAmount,
        createdAt: vote.createdAt
      }))
  });
}

export function computeMarketSocialProof(marketId) {
  const market = mockStore.markets.find((item) => item.id === marketId || item.slug === marketId);
  if (!market) return null;

  return buildMarketSocialProof({
    market,
    options: market.options,
    votes: mockStore.votes
      .filter((vote) => vote.marketId === market.id)
      .map((vote) => ({
        id: vote.id,
        optionId: vote.optionId,
        userId: vote.userId,
        voteAmount: vote.voteAmount,
        createdAt: vote.createdAt
      }))
  });
}

export function computeSeasonZeroParticipants() {
  return buildSeasonZeroParticipants({
    creatorWallets: mockStore.markets.map((market) => market.creatorWallet).filter(Boolean),
    voterWallets: mockStore.votes.map((vote) => vote.userId).filter(Boolean)
  });
}

export function computeMarketComments({ marketId, userId = null, sort = "top" } = {}) {
  const market = findMockMarket(marketId);
  if (!market) return null;

  const normalizedSort = normalizeCommentSort(sort);
  const likedIds = likedMockCommentIdsFor(userId);
  const comments = mockStore.comments
    .filter((comment) => comment.marketId === market.id)
    .sort(commentSorter(normalizedSort))
    .map((comment) =>
      formatMockComment(comment, {
        viewerWallet: userId,
        viewerLiked: likedIds.has(comment.id)
      })
    );

  return {
    marketId: market.id,
    sort: normalizedSort,
    nextCursor: null,
    viewer: {
      isAuthenticated: Boolean(userId),
      hasVotedInMarket: hasConfirmedMockVote({ marketId: market.id, userId })
    },
    comments,
    limits: { maxLength: COMMENT_MAX_LENGTH }
  };
}

export function createMockMarketComment({ marketId, userId, body }) {
  const market = findMockMarket(marketId);
  if (!market) throw new Error("Market not found.");
  if (!userId) throw new Error("Missing session.");
  if (!hasConfirmedMockVote({ marketId: market.id, userId })) {
    throw new Error("Vote in this market to post a comment.");
  }

  const { value, error } = sanitizeCommentInput({ body });
  if (error) throw new Error(error);

  const comment = {
    id: `comment-${nextCommentNumber++}`,
    marketId: market.id,
    walletAddress: userId,
    body: value.body,
    authorPositionLabel: authorPositionLabelFor({ marketId: market.id, userId }),
    likeCount: 0,
    viewerLiked: false,
    createdAt: new Date().toISOString(),
    moderationStatus: "visible"
  };
  mockStore.comments.push(comment);
  return formatMockComment(comment, { viewerWallet: userId, viewerLiked: false });
}

export function toggleMockMarketCommentLike({ marketId, commentId, userId }) {
  const comment = findMockComment({ marketId, commentId });
  if (!comment) throw new Error("Comment not found.");
  if (!userId) throw new Error("Missing session.");

  const key = `${comment.id}:${userId}`;
  const liked = mockStore.commentLikes.has(key);
  if (liked) {
    mockStore.commentLikes.delete(key);
    comment.likeCount = Math.max(0, Number(comment.likeCount || 0) - 1);
  } else {
    mockStore.commentLikes.add(key);
    comment.likeCount = Number(comment.likeCount || 0) + 1;
  }

  return {
    commentId: comment.id,
    likeCount: comment.likeCount,
    viewerLiked: !liked
  };
}

export function hideMockMarketComment({ marketId, commentId, userId }) {
  const comment = findMockComment({ marketId, commentId });
  if (!comment) throw new Error("Comment not found.");
  if (!userId) throw new Error("Missing session.");
  if (comment.walletAddress !== userId) throw new Error("Only the comment author can hide this comment.");

  comment.moderationStatus = "hidden_by_author";
  comment.hiddenAt = new Date().toISOString();
  comment.hiddenByWallet = userId;

  return formatMockComment(comment, {
    viewerWallet: userId,
    viewerLiked: likedMockCommentIdsFor(userId).has(comment.id)
  });
}

export function reportMockMarketComment({ marketId, commentId, userId, reason, details }) {
  const comment = findMockComment({ marketId, commentId });
  if (!comment) throw new Error("Comment not found.");
  if (!userId) throw new Error("Missing session.");

  const { value, error } = sanitizeReportInput({ reason, details });
  if (error) throw new Error(error);

  const duplicate = mockStore.commentReports.some(
    (report) => report.commentId === comment.id && report.walletAddress === userId && report.reason === value.reason
  );
  if (duplicate) throw new Error("You already reported this comment for that reason.");

  const report = {
    id: `comment-report-${mockStore.commentReports.length + 1}`,
    commentId: comment.id,
    walletAddress: userId,
    reason: value.reason,
    details: value.details,
    createdAt: new Date().toISOString()
  };
  mockStore.commentReports.push(report);
  return report;
}

function addDistinctUser(table, affiliateUserId, userId) {
  if (!table.has(affiliateUserId)) {
    table.set(affiliateUserId, new Set());
  }
  table.get(affiliateUserId).add(userId);
}

function addCommunitySupport(table, community, vote) {
  const key = community.slug || "kreechures";
  const entry = table.get(key) || {
    slug: key,
    name: community.name || "Kreechures",
    collectionLamports: 0,
    voteCount: 0,
    marketIds: new Set()
  };
  entry.collectionLamports += Math.max(0, Number(vote.collectionLamports || 0));
  entry.voteCount += 1;
  if (vote.marketId) entry.marketIds.add(vote.marketId);
  table.set(key, entry);
}

function communityForVote(vote, market) {
  return {
    slug: String(vote.supportCommunitySlug || market?.supportCommunitySlug || "kreechures").trim() || "kreechures",
    name: String(vote.supportCommunityName || market?.supportCommunityName || "Kreechures").trim() || "Kreechures"
  };
}

function asAffiliateRank(scoreMap, userSetMap, usersLookup) {
  return Array.from(scoreMap.entries())
    .map(([affiliateUserId, referredVotesCents]) => ({
      affiliateUserId,
      nickname: usersLookup.get(affiliateUserId)?.nickname || null,
      referredVotes: fromCents(referredVotesCents),
      referredUsers: userSetMap.get(affiliateUserId)?.size || 0
    }))
    .sort((a, b) => b.referredVotes - a.referredVotes || b.referredUsers - a.referredUsers)
    .slice(0, 20);
}

function asCommunityRank(scoreMap) {
  return Array.from(scoreMap.values())
    .sort(
      (left, right) =>
        right.collectionLamports - left.collectionLamports ||
        right.voteCount - left.voteCount ||
        left.name.localeCompare(right.name)
    )
    .slice(0, 20)
    .map((row) => ({
      slug: row.slug,
      name: row.name,
      supportedSol: lamportsToSol(row.collectionLamports),
      voteCount: row.voteCount,
      marketCount: row.marketIds.size
    }));
}

function startOfUtcWeek(timestamp) {
  const date = new Date(timestamp);
  date.setUTCHours(0, 0, 0, 0);
  const daysSinceMonday = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - daysSinceMonday);
  return date;
}

function findMockMarket(marketId) {
  return mockStore.markets.find((item) => item.id === marketId || item.slug === marketId) || null;
}

function findMockComment({ marketId, commentId }) {
  const market = findMockMarket(marketId);
  if (!market) return null;
  return mockStore.comments.find((comment) => comment.id === commentId && comment.marketId === market.id) || null;
}

function hasConfirmedMockVote({ marketId, userId }) {
  return Boolean(userId) && mockStore.votes.some((vote) => vote.marketId === marketId && vote.userId === userId);
}

function likedMockCommentIdsFor(userId) {
  const ids = new Set();
  if (!userId) return ids;

  for (const key of mockStore.commentLikes) {
    const [commentId, walletAddress] = key.split(":");
    if (walletAddress === userId) ids.add(commentId);
  }

  return ids;
}

function commentSorter(sort) {
  if (sort === "newest") {
    return (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
  }
  return (left, right) =>
    Number(right.likeCount || 0) - Number(left.likeCount || 0) ||
    new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
}

function authorPositionLabelFor({ marketId, userId }) {
  const totals = new Map();
  for (const vote of mockStore.votes.filter((row) => row.marketId === marketId && row.userId === userId)) {
    totals.set(vote.optionId, (totals.get(vote.optionId) || 0) + Number(vote.voteAmount || 0));
  }
  const top = [...totals.entries()].sort((left, right) => right[1] - left[1])[0];
  if (!top) return null;
  const market = findMockMarket(marketId);
  const option = market?.options.find((item) => item.id === top[0]);
  return option?.label || null;
}

function formatMockComment(comment, { viewerWallet = null, viewerLiked = false } = {}) {
  const hidden = comment.moderationStatus && comment.moderationStatus !== "visible";
  return {
    id: comment.id,
    marketId: comment.marketId,
    walletAddress: comment.walletAddress,
    ...displayIdentityFor(comment.walletAddress),
    body: hidden ? "Comment hidden by author." : comment.body,
    authorPositionLabel: comment.authorPositionLabel || null,
    marketSnapshotLabel: "Posted after this wallet voted in this market.",
    likeCount: Number(comment.likeCount || 0),
    viewerLiked: Boolean(viewerLiked),
    viewerCanDelete: Boolean(
      viewerWallet && viewerWallet === comment.walletAddress && comment.moderationStatus === "visible"
    ),
    createdAt: comment.createdAt,
    moderationStatus: comment.moderationStatus || "visible"
  };
}

function toCents(value) {
  return Math.round(Number(value || 0) * 100);
}

function fromCents(value) {
  return Number((Number(value || 0) / 100).toFixed(2));
}

function lamportsToSol(value) {
  return Number((Number(value || 0) / 1_000_000_000).toFixed(9));
}
